import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, Tooltip } from '@mui/material';
import { ORDER_STATUS } from 'consts';
import OrderList from 'components/OrderList';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import NotificationsContext from 'contexts/notificationsContext';
import OrdersContext from 'contexts/ordersContext';
import { ErrorOutline, Refresh } from '@mui/icons-material';
import { useMountLogs } from 'hooks';
import ApiContext from 'contexts/apiContext';
import StoreContext from 'contexts/storeContext';
import ErrorContext from 'contexts/errorContext';
import UnconfirmedOrdersDialog from 'components/UnconfirmedOrdersDialog';
import { getDateBeginningOfMonth } from 'utils';

export default function Orders() {
  const pendingStatuses = useMemo(() => [ORDER_STATUS.PENDING], []);
  const finalizedStatuses = useMemo(
    () => [ORDER_STATUS.COMPLETED, ORDER_STATUS.NOT_CONFIRMED, ORDER_STATUS.CANCELLED, ORDER_STATUS.EXPIRED],
    [],
  );
  const { orderNotification, clearOrderNotification } = useContext(NotificationsContext);
  const [shouldRefreshOrders, setShouldRefreshOrders] = useState(true);
  const [highlightOrderId, setHighlightOrderId] = useState<number>();
  const [daysBack, setDaysBack] = useState(2);
  const daysSinceStartOfMonth = new Date().getDate() - 1; // Days since the start of the month (0-indexed)
  const [unconfirmedCount, setUnconfirmedCount] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { api } = useContext(ApiContext);
  const { store } = useContext(StoreContext);
  const { setError } = useContext(ErrorContext);

  useMountLogs('OrdersScreen');

  const refreshOrders = useCallback(() => {
    setShouldRefreshOrders((prev) => !prev);
  }, []);

  // Highlight new order if it's in the URL
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const newOrderId = Number(query.get('newOrderId')); // Returns NaN (falsy) if newOrderId can't be parsed
    if (newOrderId) {
      setHighlightOrderId(newOrderId);
    }
  }, []);

  // Refresh orders and highlight new one when an order notification is received
  useEffect(() => {
    if (orderNotification) {
      refreshOrders();
      setHighlightOrderId(orderNotification.order_id);
      clearOrderNotification();
    }
  }, [orderNotification, refreshOrders, clearOrderNotification]);

  // Unconfirmed orders count
  useEffect(() => {
    api.orders
      .list(store.id, 1, 1, [ORDER_STATUS.NOT_CONFIRMED], getDateBeginningOfMonth().toISOString())
      .then((res) => {
        setUnconfirmedCount(res.data.count);
        if (res.data.count === 0) {
          setDialogOpen(false);
        }
      })
      .catch(setError);
  }, [api, store.id, setError, shouldRefreshOrders]);

  return (
    <OrdersContext.Provider value={{ shouldRefreshOrders, refreshOrders, highlightOrderId }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <FormControl sx={{ minWidth: 100 }} size='small'>
          <InputLabel>Desde</InputLabel>
          <Select value={daysBack} onChange={(e) => setDaysBack(e.target.value as number)} label='Desde'>
            <MenuItem value={1}>Hace 1 día</MenuItem>
            <MenuItem value={2}>Hace 2 días</MenuItem>
            <MenuItem value={3}>Hace 3 días</MenuItem>
            <MenuItem value={7}>Hace 7 días</MenuItem>
            <MenuItem value={daysSinceStartOfMonth}>Principio de mes</MenuItem>
          </Select>
        </FormControl>
        <Button variant='contained' startIcon={<Refresh />} onClick={refreshOrders} sx={{ ml: 2 }}>
          Refrescar
        </Button>
        {unconfirmedCount > 0 && (
          // This one is for mouse hover
          <Tooltip title='Tienes órdenes sin confirmar, por favor revísalas.' arrow>
            <Button variant='contained' color='secondary' startIcon={<ErrorOutline />} onClick={() => setDialogOpen(true)} sx={{ ml: 2 }}>
              sin confirmar
            </Button>
          </Tooltip>
        )}
      </Box>
      <Grid container spacing={8}>
        <OrderList title='Pendientes' statuses={pendingStatuses} daysBack={daysBack} />
        <OrderList title='Finalizados' statuses={finalizedStatuses} daysBack={daysBack} />
      </Grid>
      <UnconfirmedOrdersDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </OrdersContext.Provider>
  );
}
